/* eslint-disable @next/next/no-img-element */
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import Layout from '../components/Layout';
import Meta from '../components/Meta';
import Container from '../components/Container';
import Breadcrumb from '../components/Breadcrumb';
import Intro from '../components/Intro';
import Dropzone from '../components/Dropzone';
import Features from '../components/Features';
import Faq from '../components/Faq';
import FileTable from '../components/FileTable';
import ErrorModal from '../components/ErrorModal';
import useUpload from '../utils/useUpload';
import { apiUrl } from '../utils/apiRequest';
import useApiRequest from '../utils/useApiRequest';
import getErrorName from '../utils/getErrorName';
import s, { ConvertResponseStruct } from '../utils/struct';
import { sendEvent } from '../utils/analytics';
import useInterruptRouting from '../utils/useInterruptRouting';
import HowTo from "../components/HowTo";
import { Result } from '../components/Result';

const PageHtmlToPdf = () => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'jpg-to-pdf']);
  const { basePath } = useRouter();

  const [{ step, files, result }, setState] = useState({
    step: 1,
    files: [],
    result: null,
  });
  const [isStepReset, setStepReset] = useInterruptRouting(step);

  const handleGoToStep2 = useCallback((files) => {
    setStepReset(false);
    setState((state) => ({
      ...state,
      step: 2,
      files,
    }));
  }, [setStepReset]);

  const handleGoToStep3 = useCallback((result) => {
    setState((state) => ({
      ...state,
      step: 3,
      result,
    }));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    isStepReset && setState({
      step: 1,
      files: [],
      result: null,
    });
  }, [step, isStepReset]);

  let content;

  if (step === 1) {
    content = (
      <Step1
        onNextStep={handleGoToStep2}
      />
    );
  } else if (step === 2) {
    content = (
      <Step2
        files={files}
        onNextStep={handleGoToStep3}
      />
    );
  } else if (step === 3) {
    content = (
      <Step3
        result={result}
      />
    );
  }

  return (
    <Layout className="page-convert">
      <Meta
        title={t('jpg-to-pdf:meta.title')}
        description={t('jpg-to-pdf:meta.description')}
      />
      {content}
      <HowTo title={t('jpg-to-pdf:howto.title')} steps={t('jpg-to-pdf:howto.steps', {returnObjects: true})} />
      <Features
        content={t('jpg-to-pdf:features.content', { returnObjects: true })}
        style={{ backgroundImage: `url(${basePath}/images/features-3.svg)` }}
      />
      <Faq items={t('jpg-to-pdf:faq.items', { returnObjects: true })} />
    </Layout>
  );
};

const Step1 = ({ onNextStep }) => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'jpg-to-pdf']);
  const { basePath } = useRouter();
  const [files, setFiles] = useState([]);
  const { upload, uploading, error: uploadError } = useUpload();
  const [showUploadError, setShowUploadError] = useState(false);

  const handleAccepted = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      if (file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          setFiles([
            ...files,
            { file, content: reader.result },
          ]);
        };
        reader.onerror = () => {
          setFiles([
            ...files,
            { file, content: 'source file read error' },
          ]);
        };
      }
    });
  }, [files]);

  const handleUploadErrorModalHide = useCallback(() => {
    setShowUploadError(false);
  }, []);

  let errorModal;

  if (uploadError) {
    errorModal = (
      <ErrorModal
        title={t('convert-and-edit:upload-error-modal.title')}
        text={t('convert-and-edit:upload-error-modal.text')}
        show={showUploadError}
        onHide={handleUploadErrorModalHide}
      />
    );
  }

  useEffect(() => {
    const filesToUpload = files.map(({ file }) => file);

    if (!filesToUpload.length) {
      return;
    }

    upload(filesToUpload, (uploadedFiles) => {
      onNextStep(files.map((file, i) => ({
        ...file,
        hash: uploadedFiles[i].hash,
      })));
    });
  }, [files, upload, onNextStep]);

  useEffect(() => {
    if (uploadError) {
      setShowUploadError(true);
    }
  }, [uploadError]);

  return (
    <>
      <Container className="my-xs-3">
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            {t('common:nav.index')}
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            {t('common:nav.jpg-to-pdf')}
          </Breadcrumb.Item>
        </Breadcrumb>
      </Container>
      <Intro
        title={t('jpg-to-pdf:intro.title')}
        text={t('jpg-to-pdf:intro.text')}
        icon={<img src={`${basePath}/images/intro-2.svg`} width="214" alt="" />}
      />
      <Container className="my-xs-3 my-sm-4 my-md-5 my-lg-6">
        <Dropzone
          type="jpg"
          disabled={uploading}
          onAccepted={handleAccepted}
        />
      </Container>
      {errorModal}
    </>
  );
};

const Step2 = ({ files, onNextStep }) => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'jpg-to-pdf']);
  const { basePath } = useRouter();
  const [convertStatus, setConvertStatus] = useState('idle');
  const [showConvertError, setShowConvertError] = useState(false);
  const [isInvalidSize, setIsInvalidSize] = useState(false);
  const {
    sendApiRequest,
    processing,
    error: convertError,
  } = useApiRequest({ url: '/api/html/to/pdf' });
  const handleConvert = useCallback(() => {
    if (processing) {
      return;
    }

    // temporary lock to 1 file
    // max file size 20 MiB
    const file = files[0];
    if (file.file.size > 20971520) {
      setIsInvalidSize(true);
      setShowConvertError(true);
      return;
    }
    setConvertStatus('progress');

    const params = JSON.stringify({ scale: 1 });

    sendApiRequest({
      data: {
        filename: file.file.name,
        html: `<img src="${file.content}" alt="" style="max-width: 100%" />`,
        params: params,
      },
      validateResponse(response) {
        s.assert(response.data, ConvertResponseStruct);
      },
      onSuccess(response, time) {
        sendEvent({
          category: 'jpg-to-pdf',
          action: 'success',
          label: `${time}:${files.length}`,
        });

        const { url, size } = response.data.result[0];
        const fileType = 'pdf';
        const fileUrl = url;
        const fileSize = size;
        const fileName = [
          file.file.name.split('.').slice(0, -1).join('.'),
          'pdf',
        ].join('.');

        setConvertStatus('done');

        onNextStep({
          fileType,
          fileName,
          fileUrl: apiUrl(fileUrl),
          fileSize,
        });
      },
      onError(error, time) {
        sendEvent({
          category: 'jpg-to-pdf',
          action: 'error',
          label: `${getErrorName(error)}:${time}:${files.length}`,
        });

        setConvertStatus('idle');
      },
    });
  }, [files, processing, sendApiRequest, onNextStep]);

  const handleConvertErrorModalHide = useCallback(() => {
    setIsInvalidSize(false);
    setShowConvertError(false);
  }, []);

  useEffect(() => {
    if (convertError) {
      setShowConvertError(true);
    }
  }, [convertError]);

  return (
    <>
      <Intro
        title={t('jpg-to-pdf:intro.title')}
        text={t('jpg-to-pdf:intro.text')}
        icon={<img src={`${basePath}/images/intro-2.svg`} width="214" alt="" />}
      />
      <Container className="my-xs-3">
        <FileTable
          convertTo="pdf"
          fileType="jpg"
          fileName={files[0].file.name}
          fileSize={files[0].file.size}
          onConvert={handleConvert}
          convertStatus={convertStatus}
        />
      </Container>
      <ErrorModal
        title={
          isInvalidSize
            ? t('convert-and-edit:filesize-error-modal.title')
            : t('convert-and-edit:convert-error-modal.title')
        }
        text={
          isInvalidSize
            ? t('convert-and-edit:filesize-error-modal.text')
            : t('convert-and-edit:convert-error-modal.text')
        }
        show={showConvertError}
        onHide={handleConvertErrorModalHide}
      />
    </>
  );
};

const Step3 = ({ result }) => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'jpg-to-pdf']);
  const actions = {
    edit: true,
    rotate: true,
    unlock: true,
    merge: true,
    split: true,
    compress: true,
    pdf2word: true,
    pdf2excel: true,
    pdf2jpg: true,
    pdf2png: true,
    pdf2ppt: true,
  }
  return (<Result t={t} result={result} route='/jpg-to-pdf' actions={actions}/>);
};

Step1.propTypes = {
  onNextStep: PropTypes.func.isRequired,
};

Step2.propTypes = {
  files: PropTypes.arrayOf(PropTypes.shape({
    file: PropTypes.shape({
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
    }).isRequired,
    hash: PropTypes.string,
    content: PropTypes.string.isRequired,
  })).isRequired,
  onNextStep: PropTypes.func.isRequired,
};

Step3.propTypes = {
  result: PropTypes.shape({
    fileType: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    fileUrl: PropTypes.string.isRequired,
    fileSize: PropTypes.number.isRequired,
  }).isRequired,
};

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...await serverSideTranslations(locale, [
      'common',
      'convert-and-edit',
      'jpg-to-pdf',
    ]),
  },
});

export default PageHtmlToPdf;
